import { debounce } from "lodash";

export const highlightExceededText = (quill, options = {}) => {
  const {
    maxLimit,
    enableMaxLimit,
    isHighlighting,
    highlightColor = "rgba(250, 53, 35, 0.24)",
  } = options;

  if (!quill || !enableMaxLimit || isHighlighting?.value) return;

  const text = quill.getText();
  const textLength = text.endsWith("\n") ? text.length - 1 : text.length;

  const needsHighlighting = textLength > maxLimit;
  const currentlyHighlighted =
    quill.root.querySelector('span[style*="background"]') !== null;

  if (!needsHighlighting && !currentlyHighlighted) {
    return;
  }

  if (isHighlighting) {
    isHighlighting.value = true;
  }

  try {
    if (textLength <= maxLimit) {
      if (currentlyHighlighted) {
        quill.formatText(0, quill.getLength(), "background", false);
      }
    } else {
      quill.formatText(0, quill.getLength(), "background", false);

      const docLenExcludingTrailingNewline = quill.getLength() - 1;
      const exceededStart = Math.max(0, maxLimit);
      const exceededLength = Math.max(
        0,
        docLenExcludingTrailingNewline - maxLimit,
      );

      if (exceededLength > 0) {
        quill.formatText(
          exceededStart,
          exceededLength,
          "background",
          highlightColor,
        );
      }
    }
  } catch (error) {
    console.error("Error in highlighting:", error);
  } finally {
    if (isHighlighting) {
      isHighlighting.value = false;
    }
  }
};

export const createDebouncedHighlight = (quill, options = {}, delay = 100) => {
  return debounce(() => highlightExceededText(quill, options), delay);
};

export const updateCharCount = (quill, charCountRef) => {
  if (quill && charCountRef) {
    let text = quill.getText();

    if (text.endsWith("\n")) {
      text = text.slice(0, -1);
    }

    charCountRef.value = text.length;
  }
};

export const getPlainTextLength = (htmlContent) => {
  if (htmlContent == null) return 0;

  const root = document.createElement("div");
  root.innerHTML = String(htmlContent);

  const BLOCK_TAGS_WITH_NEWLINE = new Set([
    "P",
    "LI",
    "DIV",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "PRE",
    "BLOCKQUOTE",
  ]);

  const isBlockWithNewline = (nodeName) =>
    BLOCK_TAGS_WITH_NEWLINE.has(nodeName);

  const computeLength = (node, insideBlock = false) => {
    let length = 0;
    let blockEndings = 0;

    const traverse = (n, inBlock) => {
      if (n.nodeType === Node.TEXT_NODE) {
        length += n.nodeValue ? n.nodeValue.length : 0;
        return;
      }
      if (n.nodeType !== Node.ELEMENT_NODE) return;

      const nodeName = n.nodeName;

      if (nodeName === "BR") {
        if (!inBlock) {
          length += 1;
        }
        return;
      }

      const isBlock = isBlockWithNewline(nodeName);
      const nextInBlock = inBlock || isBlock;

      const childNodes = n.childNodes || [];
      for (let i = 0; i < childNodes.length; i++) {
        traverse(childNodes[i], nextInBlock);
      }

      if (isBlock) {
        blockEndings += 1;
        length += 1;
      }
    };

    traverse(node, insideBlock);

    if (blockEndings > 0 && length > 0) {
      length -= 1;
    }

    return length;
  };

  return computeLength(root, false);
};

export const convertHtmlToPlainTextWithAIVariables = (htmlContent) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  const aiVariableElements = tempDiv.querySelectorAll(
    ".ai_variable_button, ai-var, .preview_ai_variable",
  );
  aiVariableElements.forEach((element) => {
    const id = element.getAttribute("data-id");
    const original =
      element.getAttribute("data-original") || `{{ai_variable.${id}}}`;
    element.replaceWith(document.createTextNode(original));
  });
  return (tempDiv.textContent || tempDiv.innerText || "").trim();
};

export const truncatePlainTextToLimit = (htmlContent, maxLimit) => {
  if (!htmlContent || !maxLimit) return htmlContent || "";
  const plainText = convertHtmlToPlainTextWithAIVariables(htmlContent);
  return plainText.length <= maxLimit
    ? plainText
    : plainText.substring(0, maxLimit);
};

export const normalizeQuillListsHtml = (html) => {
  if (html == null) return html;
  try {
    const container = document.createElement("div");
    container.innerHTML = String(html);
    normalizeQuillListsContainer(container);
    return container.innerHTML;
  } catch {
    return html;
  }
};

export const normalizeQuillListsContainer = (container) => {
  try {
    container.querySelectorAll("li span.ql-ui").forEach((el) => el.remove());

    container.querySelectorAll("ol, ul").forEach((list) => {
      const listItems = Array.from(list.children).filter(
        (n) => n && n.nodeType === Node.ELEMENT_NODE && n.tagName === "LI",
      );

      const hasBullet = listItems.some(
        (li) => li.getAttribute && li.getAttribute("data-list") === "bullet",
      );
      const hasOrdered = listItems.some(
        (li) => li.getAttribute && li.getAttribute("data-list") === "ordered",
      );

      const isMixedList = hasBullet && hasOrdered;

      let desiredTag = list.tagName; // "OL" or "UL"
      if (hasBullet && !hasOrdered) desiredTag = "UL";
      if (hasOrdered && !hasBullet) desiredTag = "OL";

      if (desiredTag !== list.tagName) {
        const replacement = document.createElement(desiredTag);
        if (list.hasAttribute("style")) {
          replacement.setAttribute("style", list.getAttribute("style"));
        }
        while (list.firstChild) {
          const child = list.firstChild;
          if (
            child.nodeType === Node.ELEMENT_NODE &&
            child.tagName === "LI" &&
            child.hasAttribute("data-list")
          ) {
            child.removeAttribute("data-list");
          }
          replacement.appendChild(child);
        }
        list.parentNode && list.parentNode.replaceChild(replacement, list);
        list = replacement;
      }

      if (!isMixedList) {
        list
          .querySelectorAll("li")
          .forEach((li) => li.removeAttribute("data-list"));
      }
    });
  } catch {}
};
